import React, { useEffect, useMemo, useRef } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BarChart3, GaugeCircle, Table2 } from 'lucide-react';
import { CATEGORY_LABELS, type WidgetCategory, type WidgetDefinition } from '../widgetRegistry.tsx';
import { WidgetFrameProvider } from '../controller/shared.tsx';
import { getPreviewSeeds } from '../previewSampleData.ts';
import { AnalyticsScopeOverride } from '../../../hooks/analytics/useAnalyticsScope.tsx';

const CATEGORY_STYLES: Record<WidgetCategory, { icon: React.ComponentType<{ className?: string }>; badgeClass: string }> = {
  kpi: { icon: GaugeCircle, badgeClass: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  chart: { icon: BarChart3, badgeClass: 'bg-violet-50 text-violet-700 ring-violet-600/20' },
  table: { icon: Table2, badgeClass: 'bg-amber-50 text-amber-700 ring-amber-600/20' },
};

/** Small color-coded KPI/Chart/Table badge. */
export const CategoryBadge: React.FC<{ category: WidgetCategory; className?: string }> = ({ category, className = '' }) => {
  const { icon: Icon, badgeClass } = CATEGORY_STYLES[category];
  return (
    <span
      className={`analytics-category-chip inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ring-1 ring-inset ${badgeClass} ${className}`}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {CATEGORY_LABELS[category]}
    </span>
  );
};

/** Keeps one broken widget preview from taking down the whole catalog; falls back to a category icon. */
class PreviewErrorBoundary extends React.Component<
  { category: WidgetCategory; children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      const { icon: Icon } = CATEGORY_STYLES[this.props.category];
      return (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 text-slate-300">
          <Icon className="h-7 w-7" aria-hidden />
          <span className="text-[10px] font-medium text-slate-400">Preview unavailable</span>
        </div>
      );
    }
    return this.props.children;
  }
}

const PREVIEW_HEIGHT_PX = 108;
const PREVIEW_SCALE = 0.42;

/**
 * Live, non-interactive miniature of the real widget, scaled down with a CSS
 * transform. It runs in its own QueryClient pre-seeded with sample data
 * (`previewSampleData.ts`) under the widget's real query key, with the
 * analytics scope pinned to "no audit year" so the key matches the seed and
 * no request reaches the backend.
 */
export const WidgetPreview: React.FC<{ widget: WidgetDefinition }> = ({ widget }) => {
  const Component = widget.Component;

  const previewQueryClient = useMemo(() => {
    const client = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: Infinity,
          gcTime: Infinity,
          retry: false,
          refetchOnMount: false,
          refetchOnWindowFocus: false,
          // Previews only ever read seeded data: a query without a seed
          // stays empty instead of reaching the backend.
          enabled: false,
        },
      },
    });
    for (const seed of getPreviewSeeds(widget.widget_key)) {
      client.setQueryData(seed.queryKey, seed.getData());
    }
    return client;
  }, [widget.widget_key]);

  // `inert` keeps the miniature's own inputs out of the tab order; React 18
  // has no typed prop for it, so set the attribute directly.
  const frameRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    frameRef.current?.setAttribute('inert', '');
  }, []);

  return (
    <div
      ref={frameRef}
      className="relative w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-50 pointer-events-none select-none"
      style={{ height: PREVIEW_HEIGHT_PX }}
      aria-hidden="true"
    >
      <div
        className="origin-top-left bg-white"
        style={{
          width: `${100 / PREVIEW_SCALE}%`,
          height: PREVIEW_HEIGHT_PX / PREVIEW_SCALE,
          transform: `scale(${PREVIEW_SCALE})`,
        }}
      >
        <div className="h-full w-full overflow-hidden p-3">
          <PreviewErrorBoundary category={widget.category}>
            <AnalyticsScopeOverride scope={{ auditYearId: null }}>
              <QueryClientProvider client={previewQueryClient}>
                <WidgetFrameProvider embedded>
                  <Component />
                </WidgetFrameProvider>
              </QueryClientProvider>
            </AnalyticsScopeOverride>
          </PreviewErrorBoundary>
        </div>
      </div>
    </div>
  );
};
